import { CommonModule, NgClass } from '@angular/common'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import { NotificationChannel, notificationChannelDisplayName } from '@database/models/resend-confirmation-pricing'

/**
 * Shared grid of Email / WhatsApp / SMS channel cards.
 * Used by resend confirmation and invite-to-event channel steps.
 */
@Component({
  selector: 'ngx-notification-channel-picker',
  templateUrl: './notification-channel-picker.component.html',
  standalone: true,
  imports: [CommonModule, NgClass]
})
export class NotificationChannelPickerComponent {
  /** Channels to render (order preserved). */
  @Input() channels: NotificationChannel[] = []

  /** Highlight when returning from a later step (e.g. cost confirmation). */
  @Input() selectedChannel: NotificationChannel | null = null

  @Output() channelSelect = new EventEmitter<NotificationChannel>()

  readonly Channel = NotificationChannel

  /** Bound for template — same labels as resend / invite flows. */
  readonly channelDisplayName = notificationChannelDisplayName

  /** Unique id so multiple pickers on a page do not duplicate SVG clip-path ids. */
  readonly whatsappClipId = `ncp_wa_${Math.random().toString(36).slice(2, 11)}`

  onChannelClick(ch: NotificationChannel) {
    this.channelSelect.emit(ch)
  }

  /**
   * Card styles: resend flow uses selected highlight when returning from cost step.
   */
  channelCardClassList(ch: NotificationChannel): string[] {
    const base = [
      'tw-group',
      'tw-flex',
      'tw-flex-col',
      'tw-items-center',
      'tw-justify-center',
      'tw-gap-3',
      'tw-rounded-xl',
      'tw-cursor-pointer',
      'tw-transition-all',
      'tw-duration-200',
      'tw-py-8',
      'tw-px-4',
      'tw-min-h-[140px]',
      'tw-w-[240px]',
      'tw-text-left',
      'focus:tw-outline-none',
      'focus-visible:tw-ring-2',
      'focus-visible:tw-ring-blue-500',
      'focus-visible:tw-ring-offset-2'
    ]

    if (this.selectedChannel === ch) {
      return [...base, 'tw-bg-blue-50', 'tw-border-2', 'tw-border-blue-600', 'tw-shadow-sm']
    }

    return [...base, 'tw-border', 'tw-border-gray-200', 'tw-bg-white', 'hover:tw-bg-gray-50', 'hover:tw-border-gray-300']
  }
}
